import { AppAction, AppState, getStepIndex } from '../../appState'
import FormWithInput from './FormWithInput'
import FormWithRadio from './FormWithRadio'
import LandingContent from '../LandingContent'
import Result from '../result/Result'
import FormWithCheckboxes from './FormWithCheckboxes'
import FormWithDropdown from './FormWithDropdown'
import { track } from '../../lib/tracking'
import { snakeToKebab } from '../../lib/utils'

interface Props {
  state: AppState
  appDispatch: React.Dispatch<AppAction>
}

export default function StepMapper({ state, appDispatch }: Readonly<Props>) {
  const { step, userData } = state

  function validateSelectOne(value: string) {
    if (!value) {
      return 'Du måste välja ett alternativ'
    }
    return ''
  }

  if (step === 'start') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    return <LandingContent appDispatch={appDispatch} />
  }
  if (step === 'postal_code') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    return (
      <FormWithInput
        step={stepIndex}
        id={step}
        key={step}
        value={userData.postal_code}
        appDispatch={appDispatch}
        label="Vilket är ditt postnummer?"
        placeholder="123 45"
        formatFunction={(value: string) => {
          const digitsOnly = value.replace(/\D+/g, '')
          const endsWithSpace = value.endsWith(' ') && digitsOnly.length % 2 === 0
          let firstPartLength = 3
          if (endsWithSpace) {
            firstPartLength = 2
          }
          const firstPart = digitsOnly.slice(0, firstPartLength)
          const secondPart = digitsOnly.slice(firstPartLength, 5)
          return secondPart ? `${firstPart} ${secondPart}` : firstPart
        }}
        validationFunction={(value: string) => {
          if (value.length !== 5) {
            return 'Postnumret måste vara 5 siffror'
          }
          return ''
        }}
        maxLength={5}
        isValidatingPostalCode={state.isValidatingPostalCode}
      />
    )
  }
  if (step === 'construction_year') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    return (
      <FormWithDropdown
        step={stepIndex}
        id={step}
        key={step}
        value={userData.construction_year}
        appDispatch={appDispatch}
        label="När byggdes ditt hus?"
      />
    )
  }
  if (step === 'living_area') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    return (
      <FormWithInput
        step={stepIndex}
        id={step}
        key={step}
        value={userData.living_area}
        appDispatch={appDispatch}
        label="Hur stor boyta värms upp?"
        description="Exkludera t ex källare och vind om de inte värms upp "
        suffix="m²"
        placeholder="Boyta"
        validationFunction={(value: string) => {
          const number = Number(value)
          if (!isNaN(number) && number >= 1 && number <= 600) {
            return ''
          } else {
            return 'Fyll i ett värde mellan 1 och 600.'
          }
        }}
        isFetching={state.isFetching}
      />
    )
  }

  if (step === 'solar_panel_power') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    let id = 0
    return (
      <FormWithRadio
        step={stepIndex}
        id={step}
        key={step}
        value={userData.solar_panel_power}
        appDispatch={appDispatch}
        label="Har du installerat solceller?"
        description="Exkludera t ex källare och vind om de inte värms upp"

        options={[
          { id: id++, label: 'Ja', value: '8' },
          { id: id++, label: 'Nej', value: '0' },
        ]}
        validationFunction={validateSelectOne}
      />
    )
  }

  if (step === 'heat_source') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    let id = 0
    const options = [
      { id: id++, label: 'Bergvärmepump', value: 'heating-pump-ground-rock' },
      { id: id++, label: 'Direktverkande el', value: 'electricity-direct' },
      { id: id++, label: 'Flis/pellets/ved', value: 'wood-chips' },
      { id: id++, label: 'Fjärrvärme', value: 'district' },
      { id: id++, label: 'Fossila bränslen (t ex gas)', value: 'fossil' },
      { id: id++, label: 'Vattenburen el', value: 'electricity-water' },
      { id: id++, label: 'Värmepump luft/vatten', value: 'heating-pump-water' },
      { id: id++, label: 'Värmepump luft/luft', value: 'heating-pump-air' },
      { id: id++, label: 'Värmepump-frånluft', value: 'heating-pump-exhaust-air' },
      { id: id++, label: 'Värmepump mark', value: 'heating-pump-ground' },
    ]
    return (
      <FormWithCheckboxes
        step={stepIndex}
        id={step}
        key={step}
        values={userData.heat_source}
        appDispatch={appDispatch}
        label="Hur värms ditt hus upp idag?"
        options={options}
        validationFunction={(values: string[]) => {
          if (values.length === 0) {
            return 'Du måste välja minst ett alternativ'
          }
          return ''
        }}
        isFetching={state.isFetching}
      />
    )
  }

  if (step === 'result') {
    const stepIndex = getStepIndex(step)
    track('view', `${stepIndex}.${snakeToKebab(step)}`)
    return (
      <div className="flex justify-center">
        <Result resultData={state.data} userData={userData} appDispatch={appDispatch} />
      </div>
    )
  }
}
